import React from 'react';
import { handleActionError } from '../../../utils/engineering-tools';
import { BearingType, BearingTypeCode, GreaseFrequencyPayload, TimkenUnit } from '../../../../components/bearing-search/types/types';
import { convertNumberInput, isValidRange } from '../../../utils/validations';
import { bearingTypes } from '../../../../components/bearing-search/api/api';

type GreaseRelubricationPart = Pick<GreaseFrequencyPayload, 'unit' | 'bearingType' | 'bearingSubType' | 'innerDiameter' | 'outerDiameter' | 'bearingWidth' | 'bearingWeight'>;

type FillBySpecificationValidationMap = Map<keyof FillBySpecificationState, boolean>;

export type FillBySpecificationState = {
	unit: TimkenUnit;
	partType: string;
	bearingType: string;
	bearingSubType?: string;
	innerDiameter?: number;
	outerDiameter?: number;
	bearingWidth?: number;
	bearingWeight?: number;
	partData?: GreaseRelubricationPart;
	validation: FillBySpecificationValidationMap;
};

const validationFields: Array<keyof FillBySpecificationState> = [
	'innerDiameter',
	'outerDiameter',
	'bearingWidth',
	'bearingWeight',
];

export const initialFillBySpecsState: FillBySpecificationState = {
	unit: 'M',
	partType: bearingTypes?.[ 0 ]?.partType,
	bearingType: bearingTypes?.[ 0 ]?.bearingType,
	validation: new Map( validationFields.map( ( key ) => [ key, false ] ) ),
};

export type FillBySpecificationAction =
	| { type: 'change_unit'; payload: TimkenUnit }
	| { type: 'change_bearing_type'; payload: BearingType }
	| { type: 'change_number_field'; payload: [keyof FillBySpecificationState, unknown] }
	| { type: 'update_part_data' }
	| { type: 'reset'; payload?: FillBySpecificationState };

const getFillBySpecsFormData = ( state: FillBySpecificationState ): [FillBySpecificationValidationMap, GreaseRelubricationPart | undefined] => {
	const {
		unit,
		bearingType,
		bearingSubType,
		innerDiameter,
		outerDiameter,
		bearingWidth,
		bearingWeight,
	} = state;

	const validation = new Map( state.validation );

	validation.set( 'bearingWidth', isValidRange( bearingWidth, 0, Infinity, true ) );

	validation.set( 'bearingWeight', isValidRange( bearingWeight, 0, Infinity, true ) );

	validation.set( 'outerDiameter', isValidRange( outerDiameter, 0, Infinity, true ) );

	validation.set( 'innerDiameter', isValidRange( innerDiameter, 0, Infinity, true ) );

	const isFormValid = ! Array.from( validation.values() ).some( ( valid ) => ! valid );
	let partData: GreaseRelubricationPart | undefined;

	if ( isFormValid ) {
		partData = {
			unit,
			bearingType,
			bearingSubType,
			innerDiameter,
			outerDiameter,
			bearingWeight,
			bearingWidth,
		};
	}

	return [ validation, partData ];
};

const greaseFillBySpecsReducer: React.Reducer<
	FillBySpecificationState,
	FillBySpecificationAction
> = ( state, action ) => {
	switch ( action.type ) {
		case 'change_unit': {
			return {
				...state,
				unit: action.payload,
			};
		}
		case 'change_bearing_type': {
			const { bearingType, partType, bearingSubType } = action.payload;

			return {
				...state,
				bearingType,
				partType,
				bearingSubType: bearingType === BearingTypeCode.TRB ? bearingSubType : undefined,
			};
		}
		case 'change_number_field': {
			const [ key, value ] = action.payload;

			return {
				...state,
				[ key ]: convertNumberInput( value ),
			};
		}
		case 'update_part_data': {
			const [ validation, partData ] = getFillBySpecsFormData( state );

			return {
				...state,
				validation,
				partData,
			};
		}
		case 'reset': {
			const newState = action.payload ? action.payload : initialFillBySpecsState;

			return {
				...newState,
			};
		}
	}

	return handleActionError( action );
};

export default greaseFillBySpecsReducer;
